<!--
Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->
<div
    [ngClass]="data.isTransparent ? 'hig-dialog-transparent' : 'hig-dialog'"
    nvSpatialNavigationContainer
    nvTrapFocus
    [forceEnable]="data.trapFocus"
    [class.full-screen-panel-for-small-screen-devices]="data.fullscreenPanelForSmallScreen"
>
    <div
        fxLayout="row"
        fxLayoutAlign="stretch start"
        class="hig-dialog-padding"
        [class.hig-dialog-padding-small-screen]="data.fullscreenPanelForSmallScreen"
        [class.no-header-text]="!data.headerText"
        [class.no-body-text]="!data.bodyText"
        [class.no-spinner]="!data.showSpinner"
        [class.has-icon]="hasIcon"
    >
        <!-- icon -->
        @if (data.icon) {
            <i class="hig-dialog-icon material-icons">{{ data.icon }}</i>
        }
        @if (data.image && !data.image.isDraggableUrl) {
            <img alt="" class="hig-dialog-img" [src]="data.image.url" />
        }
        <div class="hig-dialog-content-container">
            <!-- header, text, link, radio options -->
            <div class="hig-dialog-info-container">
                @if (data.headerText) {
                    <div
                        class="font-header6 header-text"
                        role="heading"
                        aria-level="2"
                        [innerHTML]="data.headerText.text | translate: data.translateValues"
                    ></div>
                }
                <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    class="hig-dialog-spinner-container"
                    [ngClass]="[
                        data.bodyText ? 'hig-dialog-spinner-content-start' : 'hig-dialog-spinner-content-center',
                        data.showSpinner ? 'hig-dialog-spinner-group' : ''
                    ]"
                >
                    @if (data.showSpinner) {
                        <div class="hig-dialog-spinner">
                            <mat-progress-spinner
                                mode="indeterminate"
                                diameter="48"
                                color="accent"
                                [attr.aria-label]="'loading' | translate"
                            ></mat-progress-spinner>
                        </div>
                    }
                    <div fxLayout="column">
                        <!-- Row layout only applies with an icon, so icon-less dialogs keep the plain block flow. -->
                        @if (data.bodyText) {
                            <div [class.hig-dialog-body-row]="data.bodyIcon">
                                @if (data.bodyIcon) {
                                    <mat-icon
                                        class="hig-dialog-body-icon"
                                        [ngClass]="data.bodyIconColorTheme"
                                        [svgIcon]="data.bodyIcon"
                                        aria-hidden="true"
                                    ></mat-icon>
                                }
                                <div
                                    class="font-body2 body-text"
                                    [innerHTML]="
                                        data.bodyText.translated
                                            ? data.bodyText.text
                                            : (data.bodyText.text | translate: data.bodyText.placeholders)
                                    "
                                ></div>
                            </div>
                        }
                        @if (data.errorSubtext) {
                            <div class="font-body2 hig-dialog-storage-region-text">
                                {{ data.errorSubtext.text | translate: data.errorSubtext.placeholders }}
                            </div>
                        }
                    </div>
                </div>
                @if (data.metadata?.showMetadata) {
                    <nv-hig-list-metadata [metadata]="data.metadata"> </nv-hig-list-metadata>
                }

                <!-- table -->
                @if (data.dataTableColumns?.length > 0) {
                    <nv-hig-table-comparison
                        class="hig-dialog-table"
                        [ariaLabel]="'layout.storageUpsellDialog.ariaLabel' | translate"
                        [columns]="data.dataTableColumns"
                        (columnActionClick)="onTableCTAClick($event)"
                    >
                    </nv-hig-table-comparison>
                }
                <!-- link with placeholder values -->
                @if (data.link?.placeholders) {
                    <div class="hig-dialog-link-text">
                        @if (!data.link?.isTextWithHyperlinkTag) {
                            <a
                                target="_blank"
                                [href]="data.link.url"
                                [shortUrl]="data.link?.shortUrl"
                                class="font-body2-link"
                                (click)="onLinkClick()"
                                >{{ data.link.text | translate: data.link.placeholders }}</a
                            >
                        }
                        @if (data.link?.isTextWithHyperlinkTag) {
                            <span
                                cdkMonitorSubtreeFocus
                                target="_blank"
                                class="font-body2"
                                [innerHTML]="data.link.text | translate: data.link.placeholders"
                                (click)="onLinkClick()"
                            ></span>
                        }
                    </div>
                }
                <!-- link without placeholder values -->
                @if (shouldShowLinkRegion) {
                    <div class="hig-dialog-link-text">
                        @if (data.link.url && !data.link.routerLink && !data.link.linkClickedCallback) {
                            <a
                                target="_blank"
                                [href]="data.link.url"
                                [shortUrl]="data.link.shortUrl"
                                class="font-body2-link"
                                (click)="onLinkClick()"
                                >{{ data.link.text | translate }}</a
                            >
                        }
                        @if (data.link.routerLink && !data.link.linkClickedCallback) {
                            <a
                                [routerLink]="data.link.routerLink.commands"
                                [skipLocationChange]="data.link.routerLink.skipLocationChange"
                                class="font-body2-link"
                            >
                                {{ data.link.text | translate }}
                            </a>
                        }
                        @if (data.link.linkClickedCallback) {
                            <a
                                (click)="data.link.linkClickedCallback()"
                                class="font-body2-link"
                                role="link"
                                tabindex="0"
                            >
                                {{ data.link.text | translate }}
                            </a>
                        }
                    </div>
                }

                <!-- error code string -->
                @if (data.errorCodeText) {
                    <div class="font-body2 hig-dialog-error-text">
                        {{ data.errorCodeText.text | translate: data.errorCodeText.placeholders }}
                    </div>
                }
                <!-- generic upgrades -->
                @if (data.dataCells?.length > 0) {
                    <nv-hig-cell-comparison [data]="data.dataCells"> </nv-hig-cell-comparison>
                }
                <!-- radio options -->
                @if (data.radioButtonOptions) {
                    <mat-radio-group
                        class="hig-radio-container"
                        [(ngModel)]="data.radioOptionSelected"
                        (change)="onRadioOptionChange()"
                        nvSpatialNavigationGroup
                        navigateAction="focus"
                    >
                        @for (option of data.radioButtonOptions; track option) {
                            <mat-radio-button nvMatRadioSpatialNavigationFixup [value]="option.value">
                                <div class="hig-radio-label-container">
                                    @if (option.imageUrl) {
                                        <img alt="" class="hig-radio-option-image" [src]="option.imageUrl" />
                                    }
                                    <div dir="auto" class="hig-radio-option-label">{{ option.value }}</div>
                                </div>
                            </mat-radio-button>
                        }
                    </mat-radio-group>
                }
                @if (data.descriptionList) {
                    <div class="hig-description-list-container">
                        @for (option of data.descriptionList; track option) {
                            <div class="hig-description-list-item">
                                @if (option.matIcon) {
                                    <mat-icon
                                        class="hig-description-icon font-body2"
                                        [svgIcon]="option.matIcon"
                                    ></mat-icon>
                                }
                                <div class="hig-description-text font-body2">{{ option.text }}</div>
                            </div>
                        }
                    </div>
                }
                <!-- image -->
                @if (data.image?.isDraggableUrl) {
                    <div class="hig-dialog-image-container" fxLayout="row" fxLayoutAlign="start start">
                        <div class="hig-dialog-image-shortcut-container" fxLayout="column">
                            <a
                                class="hig-dialog-image-shortcut-link"
                                [href]="data.image.shortcutUrl"
                                (click)="(false)"
                                (dragstart)="onImageDragStart()"
                                >{{ data.image.caption | translate: data.image.captionPlaceholder
                                }}<img
                                    alt=""
                                    class="hig-dialog-image"
                                    [src]="data.image.url"
                                    [alt]="data.image.captionPlaceholder"
                            /></a>
                            <p
                                class="hig-dialog-image-caption"
                                [innerHTML]="data.image.caption | translate: data.image.captionPlaceholder"
                            ></p>
                        </div>
                        <p
                            class="hig-dialog-image-description font-body2"
                            [innerHTML]="data.image.description | translate"
                        ></p>
                    </div>
                }
                @if (data.checkbox) {
                    <div class="hig-dialog-checkbox">
                        @if (data.checkbox) {
                            <mat-checkbox [(ngModel)]="data.checkboxState" class="nv-hig">
                                <span class="font-body2">{{ data.checkbox.text | translate }}</span>
                            </mat-checkbox>
                        }
                    </div>
                }
            </div>
            <!-- action buttons -->
            <div class="hig-dialog-actions-container" fxLayout="column" fxLayoutGap="16px">
                <div class="hig-dialog-buttons-container" fxLayoutAlign="end center" fxLayoutGap="8px">
                    @if (data.secondaryButton) {
                        <button
                            mat-button
                            class="hig-dialog-secondary-button font-button2"
                            (click)="onSecondaryButtonClick($event)"
                            [disabled]="data.secondaryButton.isDisabled"
                        >
                            {{ data.secondaryButton.text | translate }}
                            <ng-container
                                [ngTemplateOutlet]="buttonSpinner"
                                [ngTemplateOutletContext]="{ visible: data.secondaryButton.spinnerOverlay }"
                            >
                            </ng-container>
                        </button>
                    }
                    @if (data.primaryButton) {
                        <button
                            mat-button
                            [ngClass]="
                                data.primaryButton.accentColor
                                    ? 'font-button2 mat-flat-button mat-accent'
                                    : 'font-button2'
                            "
                            [nvFocusRipple]="!!data.primaryButton.accentColor"
                            (click)="onPrimaryButtonClick($event)"
                            [disabled]="data.primaryButton.isDisabled"
                            [nvAutoFocus]="autoFocusOnPrimaryButton"
                            [ngStyle]="{ 'pointer-events': data.primaryButton.spinnerOverlay ? 'none' : 'all' }"
                            [attr.id]="data.primaryButton.id || null"
                            [attr.aria-label]="
                                data.primaryButton.removeButtonTextOnClick
                                    ? (data.primaryButton.text | translate)
                                    : null
                            "
                        >
                            <div
                                class="hig-dialog-primary-button-content"
                                [ngStyle]="{
                                    visibility: data.primaryButton.removeButtonTextOnClick ? 'hidden' : 'visible'
                                }"
                            >
                                @if (data.primaryButton.svgIcon) {
                                    <mat-icon
                                        class="hig-dialog-primary-button-icon"
                                        [svgIcon]="data.primaryButton.svgIcon"
                                        aria-hidden="true"
                                    ></mat-icon>
                                }
                                <span>{{ data.primaryButton.text | translate }}</span>
                            </div>
                            <ng-container
                                [ngTemplateOutlet]="buttonSpinner"
                                [ngTemplateOutletContext]="{ visible: data.primaryButton.spinnerOverlay }"
                            ></ng-container>
                        </button>
                    }
                </div>

                <ng-template #buttonSpinner let-visible="visible">
                    @if (visible) {
                        <div fxLayoutAlign="center center" class="spinner-overlay">
                            <mat-progress-spinner
                                [ngClass]="!!data.spinnerClass ? data.spinnerClass : 'mat-accent'"
                                mode="indeterminate"
                                [diameter]="16"
                                aria-hidden="true"
                            ></mat-progress-spinner>
                        </div>
                    }
                </ng-template>
            </div>
        </div>
    </div>
</div>
